import { FC, useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  ActionButton,
  checkLockOnPage,
  ContentSegmentTotalEntries,
  DataSubjectFormValidDataType,
  DataSubjectValidFormDataSchema,
  DetailLayout,
  EditLock,
  EMPTY_PAGE,
  Form,
  FormAlerts,
  FormSegmentLabel,
  FormSegments,
  FormSubmitHandler,
  InfoIconUrl,
  ObjectHeader,
  ObjectType,
  Padding,
  PermissionCode,
  Spinner,
  useDataSubjectHistoryQuery,
  useDataSubjectMutation,
  useDataSubjectQuery,
  useHasPermission,
  useObjectLockQuery,
  useQueryParams,
  useAlertContext,
} from '@atlas-frontend/sdk';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { path } from '../../paths';
import { UiLink } from '../../paths/UiLink';
import { useNavigateTo } from '../../paths/useNavigateTo';
import { Forbidden } from '../Forbidden';
import { DataSubjectBreadcrumbs } from './DataSubjectBreadcrumbs';
import { DataSubjectDescription } from './DataSubjectDescription';
import translations from './DataSubjectEdit.translations.json';
import { NotFound } from '../NotFound';
import { Badge, Flex } from 'antd';
import { DataSubjectDetailsContextProvider } from './DataSubjectDetailsContext';
import { DataSubjectHistoryTable } from './DataSubjectHistoryTable';
import { DataSubjectCommentsEditTable } from './table/DataSubjectCommentsEditTable';

const { addTranslations, useTranslation } = i18nNsFactory(
  'data-subject-detail',
);
addTranslations(translations);

export const DataSubjectEdit: FC = () => (
  <Forbidden isAllowed={useHasPermission(PermissionCode.SUBJECT_MANAGE)}>
    <DataSubjectEditInner />
  </Forbidden>
);

const DataSubjectEditInner: FC = () => {
  const { t } = useTranslation();
  const params = useParams<{ id: string }>();
  const dataSubjectId = Number(params.id);

  const navigateToDataSubjectView = useNavigateTo(path.dataSubjectView, {
    id: dataSubjectId,
  });
  const { resetAlert, setSuccess } = useAlertContext();
  const {
    data: dataSubject,
    refetch,
    isLoading,
  } = useDataSubjectQuery({ id: dataSubjectId });

  const {
    data: { content: dataSubjectHistory } = {},
    isLoading: isLoadingHistory,
  } = useDataSubjectHistoryQuery(
    { id: dataSubject?.id || 0, page: 0, size: 10_000 },
    { enabled: Boolean(dataSubject?.id), initialData: EMPTY_PAGE },
  );

  const onSuccess = useCallback(() => {
    setSuccess();
    void refetch();
  }, [refetch, setSuccess]);
  const { mutateAsync, isPending: isMutating } = useDataSubjectMutation({
    onSuccess,
  });

  const { data: lockData } = useObjectLockQuery({
    objectId: dataSubjectId,
    objectType: ObjectType.DataSubject,
  });
  checkLockOnPage(lockData, navigateToDataSubjectView);

  const handleSubmit: FormSubmitHandler<DataSubjectFormValidDataType> =
    useCallback(
      (data) => {
        const { comments } = data;
        const formData = comments.map((c) => ({
          id: c.id,
          comments: c.comments,
        }));

        resetAlert();
        return mutateAsync({
          id: dataSubjectId,
          data: { comments: formData },
        });
      },
      [dataSubjectId, mutateAsync, resetAlert],
    );

  // TODO: Sorting is temporary until we can fix sorting issues in the table
  const formValues = useMemo(() => {
    return {
      comments: dataSubject?.comments
        .map(({ id, comments, createdAt, createdBy, modifiedAt }) => ({
          id,
          comments,
          createdBy,
          createdAt,
          modifiedAt,
        }))
        .sort((a, b) => b.createdAt!.localeCompare(a.createdAt!)),
    };
  }, [dataSubject?.comments]);

  const [commentsCount, setCommentsCount] = useState<number | undefined>();
  const handleChangeComments = useCallback(
    (comments: DataSubjectFormValidDataType[]) => {
      setCommentsCount(comments.length);
    },
    [],
  );

  const segments = useMemo(
    () => [
      {
        value: 'history',
        label: (
          <Flex gap="small" align="center">
            {t('segment.history')} <Badge count={dataSubjectHistory!.length} />
          </Flex>
        ),
        count: dataSubjectHistory!.length,
        children: (
          <DataSubjectHistoryTable
            data={dataSubjectHistory!}
            isLoading={isLoadingHistory}
          />
        ),
      },
      {
        value: 'comments',
        label: (
          <FormSegmentLabel segmentValue="comments" formFieldName="comments">
            {t('segment.comments')}
          </FormSegmentLabel>
        ),
        count: commentsCount,
        children: (
          <DataSubjectCommentsEditTable onChange={handleChangeComments} />
        ),
      },
    ],
    [
      t,
      isLoadingHistory,
      dataSubjectHistory,
      commentsCount,
      handleChangeComments,
    ],
  );

  const [{ segment }] = useQueryParams();

  if (isLoading || isLoadingHistory) {
    return <Spinner />;
  }

  if (!dataSubject) {
    return <NotFound />;
  }

  return (
    <DataSubjectDetailsContextProvider>
      <Form
        defaultValues={formValues}
        schema={DataSubjectValidFormDataSchema}
        key={JSON.stringify(formValues)}
        onSubmit={handleSubmit}
      >
        <DetailLayout
          infoIconUrl={InfoIconUrl.DATA_SUBJECT_DETAILS}
          breadCrumb={<DataSubjectBreadcrumbs data={{ dataSubject }} />}
          headerConfig={{
            actions: (
              <>
                <UiLink
                  id="mainAction-link"
                  path={path.dataSubjectView}
                  params={
                    segment
                      ? { id: dataSubject.id, segment }
                      : { id: dataSubject.id }
                  }
                >
                  {({ type, ...rest }) => (
                    <ActionButton layoutType="detail" type="abort" {...rest} />
                  )}
                </UiLink>
                <ActionButton type="formSubmit" loading={isMutating} />
              </>
            ),
          }}
          side={{
            header: (
              <Padding>
                <ObjectHeader
                  upperText={dataSubject?.code}
                  lowerText={dataSubject?.name}
                />
              </Padding>
            ),
            content: dataSubject && (
              <DataSubjectDescription data={dataSubject} isEditable />
            ),
            footer: (
              <EditLock
                entity={dataSubject}
                objectType={ObjectType.DataSubject}
                onTimeout={navigateToDataSubjectView}
              />
            ),
          }}
          alert={<FormAlerts />}
          footerContent={<ContentSegmentTotalEntries segments={segments} />}
        >
          <Padding>
            <FormSegments segments={segments} />
          </Padding>
        </DetailLayout>
      </Form>
    </DataSubjectDetailsContextProvider>
  );
};
